import { z } from 'zod';

const REQUIRED = 'general_label_required';
const MUST_BE_NUMBER = 'bulk_invite_error_invalid_number';

/**
 * Veritix systems carry connection credentials that the create drawer already
 * collects and marks `required` on every one of its `Form.Item`s, and that the
 * submit handler posts verbatim to `POST /ticketing/systems`. They were
 * previously unvalidated, so a Veritix system could be created with blank
 * credentials.
 *
 * `directSettlementPaymentTypeId` is rendered as a plain text input, so form
 * state holds whatever was typed; both shapes are accepted so the payload is
 * unchanged. The backend DTO types it as a Java `int`, so it must additionally
 * be all digits — Jackson coerces "123" but throws InvalidFormatException on
 * anything else, which surfaced as a 500 with a raw stack trace and no
 * user-facing error. `usingMock` is a switch with no default, hence optional.
 */
const veritixConfigurationSchema = z.object({
  login: z.string().min(1, REQUIRED),
  password: z.string().min(1, REQUIRED),
  endpointURL: z.string().min(1, REQUIRED),
  directSettlementPaymentTypeId: z
    .union([z.string(), z.number()])
    .refine((value) => String(value).trim() !== '', REQUIRED)
    .refine((value) => /^\d+$/.test(String(value).trim()), MUST_BE_NUMBER),
  usingMock: z.boolean().optional(),
});

const VeritixTicketingSystemSchema = z.object({
  name: z.string(),
  type: z.literal('VERITIX'),
  veritixConfiguration: veritixConfigurationSchema,
});

const UploadTicketingSystemSchema = z.object({
  name: z.string(),
  type: z.literal('UPLOAD'),
});

/**
 * Discriminated on `type` so the Veritix credentials are required only when
 * Veritix is the selected system — the two variants the drawer offers.
 */
export const TicketingSystemValidatorSchema = z.discriminatedUnion('type', [
  VeritixTicketingSystemSchema,
  UploadTicketingSystemSchema,
]);

export type TicketingSystemValidator = z.infer<
  typeof TicketingSystemValidatorSchema
>;
